/**
 * Simple feature-level sync configuration
 */
export interface FeatureSyncConfig {
  /**
   * Whether sync is enabled for this feature
   * Defaults to false if not specified
   */
  enabled?: boolean;

  /**
   * Action types from this feature to exclude from synchronization
   */
  blacklist?: string[];

  /**
   * Action types from this feature to include in synchronization
   * If provided, only these actions will be synchronized
   */
  whitelist?: string[];
}

/**
 * Default sync configuration - disabled by default for security
 */
export const DEFAULT_SYNC_CONFIG: FeatureSyncConfig = {
  enabled: false,
};

/**
 * Shared blacklist for redux-state-sync
 * Actions that should never be synchronized across tabs
 */
export const syncBlacklist: string[] = [
  // Redux persist actions should always be excluded
  'persist/PERSIST',
  'persist/REHYDRATE',
  'persist/PAUSE',
  'persist/FLUSH',
  'persist/PURGE',
  'persist/REGISTER',

  // RTK Query actions should be excluded as they contain non-serializable data
  'api/', // Matches all RTK Query actions
  'executeQuery/', // Specific query actions
  'executeMutation/', // Specific mutation actions
  'invalidateTags/', // Cache invalidation actions
  'internalSubscriptions/', // Subscription actions

  // Actions that are purely local and shouldn't sync
  '@@redux-state-sync/', // Prevent sync loops
];

/**
 * Actions that should be synchronized across tabs
 * Keep this list minimal and focused on UI state that should be consistent
 *
 * Note: Features can add their actions dynamically via addToSyncWhitelist()
 * Static actions that should always sync are listed here
 */
export const syncWhitelist: string[] = [
  // Workflow purge action - must sync to clear workflow across all tabs
  'encounters/workflow/PERSIST_CLEAR',
];

/**
 * Adds action types to the sync blacklist if they're not already present
 */
export function addToSyncBlacklist(actionTypes: string[]): void {
  if (!actionTypes || !actionTypes.length) return;

  actionTypes.forEach(actionType => {
    if (!syncBlacklist.includes(actionType)) {
      syncBlacklist.push(actionType);
    }
  });
}

/**
 * Adds action types to the sync whitelist if they're not already present
 */
export function addToSyncWhitelist(actionTypes: string[]): void {
  if (!actionTypes || !actionTypes.length) return;

  actionTypes.forEach(actionType => {
    if (!syncWhitelist.includes(actionType)) {
      syncWhitelist.push(actionType);
    }
  });
}
